import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import { Provider, useSetAtom } from 'jotai';
import { globalFilterAtom } from '@src/atoms/page';
import { SummaryContainer } from '@components/summary-panel';
import { MetricsChartContainer as HzMetricsChartContainer } from '@components/metrics-panel-hz';
import { ErrorBoundary, PageTitle, Footer, TimePanel } from '@components/common';
import { CollapseTrigger } from '@components/common/Collapse';
import { changeCollapseStatus } from '@store/common/actions';
import { clearModifyComparison } from '@store/comparison/actions';
import WidgetPanelContainer from '@components/top-widget-panel';
import { TAB_TRENDS_KEY, DATE_SHORTCUTS, SAVE_SERVICE_DASHBOARD_KEYS } from '@utils/constants';
import { metricPanelFlag } from '@utils/collapseConstants';
import { selector as commonSelector } from '@store/common';
import { getFirstAppliedFilter } from '@store/common/selectors';
import RetryContext from '@src/contexts/retryContext';
import DimensionContainer from '@components/dimension-panel-hz';
import MetricFilterBar from '@components/summary-panel/MetricFilterBar';
import { appExperienceShareConfig } from '@components/share/utils';
import InitShare from '@components/share/InitShare';
import InitSave from '@components/saving/InitSave';
import { getPageKeyForSaveService } from '@utils/savedDashboard';
import { savingSelector } from '@store/trends/dashboard/selectors';
import useInitSave from '@components/saving/useInitSave';
import AlertPanel from '@components/alert-panel';
import { buildDashboardStore } from '@src/atoms/utils';
import { setSummaryWidgets } from '@store/summaryWidgets/actions';
import { SPI_METRIC } from '@components/top-widget-panel/spi-panel/widgets/constants';
import { DEFAULT_WIDGETS, SUMMARY_PANEL_TYPE } from '@components/top-widget-panel/constants';
import { summaryWidgetsSelector } from '@store/summaryWidgets/selector';
import CompareFiltersBar from '@components/summary-panel/CompareFiltersBar';

import { ContentWrapper, HzMetricsPanelWrapper } from '../main/styled';
import { useSetError } from '@src/hooks/useSetError';
import { useResetForPreset } from '@src/hooks/useResetForPreset';

const defaultStore = buildDashboardStore();

const AppExperience = (props) => {
  useSetError();
  const dispatch = useDispatch();
  const {
    isSharing,
    match,
    collapseStatus,
    isSavedDashboard = false, // added from savedDashboardHOC
    savedDashboardCreatorId, // added from savedDashboardHOC
    title = 'Trends',
  }: any = props;
  const setGlobalFilter = useSetAtom(globalFilterAtom);
  const appliedFilter = useSelector(getFirstAppliedFilter);
  setGlobalFilter(appliedFilter);

  useEffect(() => {
    return () => {
      dispatch(clearModifyComparison());
    };
  }, []);

  useResetForPreset();

  const { interval } = useSelector(commonSelector);
  const id = get(match, 'params.id', TAB_TRENDS_KEY); // default home page is trends
  const liveModeSelected = interval.shortcut === DATE_SHORTCUTS.last15Mins;
  const savingState = useSelector(savingSelector);

  const pageTypeForSaveService = isSavedDashboard
    ? 'saved'
    : isSharing
      ? 'sharing'
      : SAVE_SERVICE_DASHBOARD_KEYS.APP_EXPERIENCE;
  const pageKeyForSaveService = getPageKeyForSaveService(
    pageTypeForSaveService,
    id,
    SAVE_SERVICE_DASHBOARD_KEYS.APP_EXPERIENCE,
  );
  useInitSave(pageKeyForSaveService);

  const summaryWidgets = useSelector(summaryWidgetsSelector);
  // HP-4563 hacky fix for App SPI Widgets
  if (isSavedDashboard || isSharing) {
    if (Array.isArray(summaryWidgets) && summaryWidgets[0].config.metrics[0] !== SPI_METRIC) {
      dispatch(
        setSummaryWidgets({
          type: SUMMARY_PANEL_TYPE.SPI,
          widgets: DEFAULT_WIDGETS.SPI,
        }),
      );
    } else if (
      Array.isArray(summaryWidgets.widgets) &&
      summaryWidgets.type !== SUMMARY_PANEL_TYPE.NETWORK &&
      summaryWidgets.widgets[0].config.metrics[0] !== SPI_METRIC
    ) {
      dispatch(
        setSummaryWidgets({
          type: SUMMARY_PANEL_TYPE.SPI,
          widgets: DEFAULT_WIDGETS.SPI,
        }),
      );
    }
  }

  return (
    <RetryContext.Provider value={{ interval }}>
      <ErrorBoundary>
        <PageTitle text={title} />
        <TimePanel />
        <SummaryContainer
          isSharing={isSharing}
          id={id}
          liveModeSelected={liveModeSelected}
          isSavedDashboard={isSavedDashboard}
          pageKeyForSaveService={pageKeyForSaveService}
          savingState={savingState}
          savedDashboardCreatorId={savedDashboardCreatorId}
        />
        <CompareFiltersBar />
        <WidgetPanelContainer />
        <ContentWrapper layout={true}>
          <MetricFilterBar />
          <HzMetricsPanelWrapper>
            <AlertPanel />
            <HzMetricsChartContainer />
          </HzMetricsPanelWrapper>
          <CollapseTrigger
            isCollapsed={collapseStatus[metricPanelFlag]}
            triggerAction={() => dispatch(changeCollapseStatus(metricPanelFlag))}
          />
          <DimensionContainer />
          <InitShare config={appExperienceShareConfig} />
          <InitSave
            pageMeta={{
              page: {
                key: pageKeyForSaveService,
              },
            }}
          />
        </ContentWrapper>
        <Footer />
      </ErrorBoundary>
    </RetryContext.Provider>
  );
};

const WrappedAppExperience = (props) => {
  const { store = defaultStore }: any = props;

  return (
    <Provider store={store}>
      <AppExperience {...props} />
    </Provider>
  );
};

WrappedAppExperience.defaultProps = {
  isSharing: false,
  match: {},
  collapseStatus: {},
};

export default WrappedAppExperience;
